import { GAME_TYPE_LABELS } from '../scheduling/GameTypeBadge';

const REGULAR_GAME_TYPE = 0;

// Home dates are tenant-local wall-clock times. UTC tenants serialize with a trailing `Z`, other
// tenants without an offset, so any offset is ignored. Values are stored as UTC instants holding
// the wall-clock components and always formatted/grouped in UTC, making them browser-timezone independent.
const WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

export const parseHomeDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const match = WALL_CLOCK_PATTERN.exec(value);
  if (!match) return null;
  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatHomeDate = (date) => (date
  ? date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '');

export const formatHomeTime = (date) => (date
  ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).toLowerCase()
  : '');

const dayKey = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const startOfWallClockDay = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/** Today's date in the tenant timezone, as a wall-clock value comparable with parseHomeDate results. */
export const getTenantToday = (timezone, now = new Date()) => {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(now)
        .map((part) => [part.type, part.value])
    );
    return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  } catch {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }
};

/** "Today" / "Tomorrow" / "in N days", where `today` comes from getTenantToday. */
export const getRelativeDayLabel = (date, today) => {
  if (!date || !today) return '';
  const days = Math.round((startOfWallClockDay(date) - startOfWallClockDay(today)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `in ${days} days`;
};

/** Label for game types worth calling out (everything except regular league games). */
export const getSpecialGameTypeLabel = (gameType) => {
  if (gameType === null || gameType === undefined || gameType === REGULAR_GAME_TYPE) return null;
  return GAME_TYPE_LABELS[gameType] || null;
};

export const isSpare = (game) => game.roleType === 'spare';

/** Spare entries are hidden for leagues where the user already has a player game. */
export const getVisibleNextGames = (myNextGames = []) => {
  const playerLeagueIds = new Set(myNextGames.filter((g) => !isSpare(g)).map((g) => g.leagueId));
  return myNextGames.filter((g) => !(isSpare(g) && playerLeagueIds.has(g.leagueId)));
};

/** The earliest upcoming player game, highlighted in the hero card. */
export const getHeroGame = (myNextGames = []) => myNextGames
  .filter((g) => !isSpare(g) && parseHomeDate(g.gameStart))
  .sort((a, b) => parseHomeDate(a.gameStart) - parseHomeDate(b.gameStart))[0] || null;

export const gameRsvpKey = (game) => (game.gameId ? `game-${game.gameId}` : `game-league-${game.leagueId}`);
export const spareSlotRsvpKey = (timeSlotId) => `slot-${timeSlotId}`;
export const socialRsvpKey = (eventId) => `social-${eventId}`;

/**
 * Merge player games, spare time slots and social events into one chronological list.
 * Each item: { key, kind: 'game' | 'spare' | 'social', start: Date, attending, ...source fields }.
 */
export const buildAgendaItems = (homeData) => {
  if (!homeData) return [];
  const items = [];

  getVisibleNextGames(homeData.myNextGames || []).forEach((game) => {
    if (isSpare(game)) {
      (game.spareTimeSlots || []).forEach((slot) => {
        const start = parseHomeDate(slot.startTime);
        if (!start) return;
        items.push({
          key: spareSlotRsvpKey(slot.timeSlotId),
          kind: 'spare',
          start,
          attending: slot.isAttending ?? null,
          leagueId: game.leagueId,
          leagueName: game.leagueName,
          leagueSlug: game.leagueSlug,
          gameType: slot.gameType,
          timeSlotId: slot.timeSlotId,
          sparePoolMemberId: game.sparePoolMemberId,
          availabilityLink: game.availabilityLink,
        });
      });
      return;
    }

    const start = parseHomeDate(game.gameStart);
    if (!start) return;
    items.push({
      ...game,
      key: gameRsvpKey(game),
      kind: 'game',
      start,
      attending: game.isAttending ?? null,
    });
  });

  (homeData.upcomingSocialEvents || []).forEach((ev) => {
    const start = parseHomeDate(ev.start);
    if (!start) return;
    items.push({
      ...ev,
      key: socialRsvpKey(ev.id),
      kind: 'social',
      start,
      attending: ev.isAttending ?? null,
    });
  });

  return items.sort((a, b) => a.start - b.start);
};

/** Group chronologically sorted agenda items by local calendar day. */
export const groupAgendaByDay = (items = []) => {
  const groups = [];
  items.forEach((item) => {
    const key = dayKey(item.start);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, date: item.start, items: [item] });
    }
  });
  return groups;
};
